import { useEffect, useState } from 'react';
import {
  listMyTreatments,
  createMyTreatment,
  updateMyTreatment,
  deleteMyTreatment,
  getMyPendingChange,
  type ProTreatment,
} from '../../lib/professional-api';
import { ThemedSelect } from './ThemedSelect';
import { apiErrorMessage } from '../../lib/api';
import { toast } from '../../lib/toast';

const money = (n: number) => `£${Number(n || 0).toFixed(2)}`;
const formatDuration = (mins: number) => {
  if (!mins || mins < 1) return '';
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const rest = mins % 60;
  return rest === 0 ? `${h}h` : `${h}h ${rest}m`;
};

type TreatmentDraft = {
  service_category_id: string;
  name: string;
  price: string;
  duration_minutes: string;
  requires_patch_test: boolean;
};

const BLANK: TreatmentDraft = {
  service_category_id: '',
  name: '',
  price: '',
  duration_minutes: '',
  requires_patch_test: false,
};

type TreatmentCategoryOption = { id: string; name: string };

/**
 * Pro-side management of their own main treatments (e.g. "Balayage",
 * "Gel manicure"). Self-serve CRUD — these go live INSTANTLY (no admin
 * approval) and show on the pro's public profile as bookable services.
 * Restricted to TREATMENT-flow categories (Hair / Beauty & Wellness).
 */
export function ProTreatmentsManager({
  categories,
}: {
  categories: TreatmentCategoryOption[];
}) {
  const [treatments, setTreatments] = useState<ProTreatment[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // null = no form open · 'new' = add form · <id> = editing that row
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<TreatmentDraft>(BLANK);
  // Treatment edits by a live (APPROVED) pro are held for admin review as
  // `pending_treatments` ops on the change request — a CREATE doesn't exist
  // in the live list yet, and an UPDATE/DELETE hasn't applied. We overlay
  // these so a just-submitted change is visible + clearly marked pending,
  // instead of looking like nothing happened.
  const [pendingOps, setPendingOps] = useState<any[]>([]);

  const load = async () => {
    setLoading(true);
    try {
      const [res, pc] = await Promise.all([
        listMyTreatments(),
        getMyPendingChange().catch(() => ({ status: false, data: null })),
      ]);
      setTreatments(res.data ?? []);
      const ops = (pc as any)?.data?.payload?.pending_treatments;
      setPendingOps(Array.isArray(ops) ? ops : []);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not load your treatments.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const startAdd = () => {
    setEditingId('new');
    setDraft({ ...BLANK, service_category_id: categories[0]?.id ?? '' });
  };
  const startEdit = (t: ProTreatment) => {
    setEditingId(t.id);
    setDraft({
      service_category_id: t.service_category_id ?? categories[0]?.id ?? '',
      name: t.name,
      price: String(t.price),
      duration_minutes: String(t.duration_minutes),
      requires_patch_test: !!t.requires_patch_test,
    });
  };
  const cancel = () => {
    setEditingId(null);
    setDraft(BLANK);
  };

  const save = async () => {
    if (saving) return;
    const name = draft.name.trim();
    const price = Number(draft.price);
    const duration = parseInt(draft.duration_minutes, 10);
    if (!draft.service_category_id) {
      toast.error('Pick a category for this treatment.');
      return;
    }
    if (!name) {
      toast.error('Enter a treatment name.');
      return;
    }
    if (!Number.isFinite(price) || price <= 0) {
      toast.error('Enter a valid price.');
      return;
    }
    if (!Number.isFinite(duration) || duration < 5) {
      toast.error('Enter how long it takes (at least 5 minutes).');
      return;
    }
    setSaving(true);
    try {
      if (editingId === 'new') {
        const res = await createMyTreatment({
          service_category_id: draft.service_category_id,
          name,
          price,
          duration_minutes: duration,
          requires_patch_test: draft.requires_patch_test,
        });
        toast.success(
          res?.pending_review
            ? 'Treatment submitted — it will go live once an admin approves it.'
            : 'Treatment added.',
        );
      } else if (editingId) {
        const res = await updateMyTreatment(editingId, {
          service_category_id: draft.service_category_id,
          name,
          price,
          duration_minutes: duration,
          requires_patch_test: draft.requires_patch_test,
        });
        toast.success(
          res?.pending_review
            ? 'Changes submitted — they will go live once an admin approves them.'
            : 'Treatment updated.',
        );
      }
      cancel();
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not save your treatment.'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (t: ProTreatment) => {
    if (!window.confirm(`Remove "${t.name}"?`)) return;
    try {
      const res = await deleteMyTreatment(t.id);
      toast.success(
        res?.pending_review
          ? 'Removal submitted — it will be removed once an admin approves it.'
          : 'Treatment removed.',
      );
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not remove the treatment.'));
    }
  };

  // No TREATMENT-flow categories means there's nothing the pro can author
  // here (they only offer hourly session services). Hide the whole block.
  if (categories.length === 0) return null;

  // Overlay the admin-pending ops: update/delete keyed by the live row id
  // (so we can badge the affected row), and creates rendered as extra
  // "pending" rows (they don't exist in the live list yet).
  const opByLiveId = new Map<string, any>();
  for (const o of pendingOps) {
    if (o?.id && (o.op === 'update' || o.op === 'delete')) {
      opByLiveId.set(o.id, o);
    }
  }
  const pendingCreates = pendingOps.filter((o) => o?.op === 'create');
  const hasPending = pendingOps.length > 0;

  return (
    <section className="mt-12">
      <div className="bg-[#f2eee2] px-6 py-3 flex items-center justify-between">
        <p className="font-bold text-[14px] text-[#8e2a2f]">Your treatments</p>
        {editingId === null ? (
          <button
            type="button"
            onClick={startAdd}
            className="h-[32px] px-4 rounded-[8px] bg-[#8e2a2f] text-white text-[12px] font-bold hover:bg-[#7a2228] transition-colors"
          >
            + Add treatment
          </button>
        ) : null}
      </div>

      <p className="px-6 pt-3 text-[12px] text-[#908e8e]">
        Your own bookable treatments (e.g. "Balayage", "Gel manicure"). You set
        the price and how long each takes. Changes are reviewed by our team
        before they go live on your public profile. Clients pay the platform
        fee + VAT on top, the same as your other services.
      </p>

      {hasPending ? (
        <div className="mx-6 mt-3 rounded-[10px] border border-[#f3d6a0] bg-[#fff7e6] px-4 py-3">
          <p className="text-[13px] text-[#9a6700]">
            You have treatment changes{' '}
            <span className="font-semibold">awaiting admin approval</span>.
            They&apos;re shown below marked{' '}
            <span className="font-semibold">Pending approval</span> and go live
            once our team approves them.
          </p>
        </div>
      ) : null}

      {loading ? (
        <p className="px-6 py-6 text-sm text-[#908e8e]">Loading…</p>
      ) : (
        <div className="divide-y divide-[#eee]">
          {treatments.length === 0 &&
          editingId !== 'new' &&
          pendingCreates.length === 0 ? (
            <p className="px-6 py-6 text-sm text-[#908e8e]">
              You haven't added any treatments yet.
            </p>
          ) : null}

          {treatments.map((t) =>
            editingId === t.id ? (
              <TreatmentForm
                key={t.id}
                draft={draft}
                setDraft={setDraft}
                categories={categories}
                onSave={save}
                onCancel={cancel}
                saving={saving}
              />
            ) : (
              <div
                key={t.id}
                className="flex items-center justify-between gap-3 px-6 py-4"
              >
                <div className="min-w-0">
                  <p className="font-bold text-[15px] text-[#3a3335] truncate">
                    {t.name}
                    {t.status === 'PENDING_APPROVAL' ? (
                      <span className="ml-2 inline-block rounded-full bg-[#fef3c7] px-2 py-0.5 text-[10px] font-bold text-[#92400e] align-middle">
                        Pending approval
                      </span>
                    ) : opByLiveId.get(t.id)?.op === 'update' ? (
                      <span className="ml-2 inline-block rounded-full bg-[#fff7e6] px-2 py-0.5 text-[10px] font-bold text-[#9a6700] align-middle ring-1 ring-inset ring-[#f3d6a0]">
                        Edit pending approval
                      </span>
                    ) : opByLiveId.get(t.id)?.op === 'delete' ? (
                      <span className="ml-2 inline-block rounded-full bg-[#fde8e8] px-2 py-0.5 text-[10px] font-bold text-[#b42318] align-middle ring-1 ring-inset ring-[#f3b4b4]">
                        Removal pending approval
                      </span>
                    ) : null}
                  </p>
                  <p className="text-[13px] text-[#908e8e]">
                    {t.category_name ? `${t.category_name} · ` : ''}
                    {money(t.price)} · {formatDuration(t.duration_minutes)}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => startEdit(t)}
                    disabled={editingId !== null}
                    className="h-[34px] px-4 rounded-[8px] border border-[#ede8e9] text-[#3a3335] text-[12px] font-bold hover:border-[#8e2a2f] hover:text-[#8e2a2f] transition-colors disabled:opacity-50"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(t)}
                    disabled={editingId !== null}
                    className="h-[34px] px-4 rounded-[8px] border border-[#ede8e9] text-[#908e8e] text-[12px] font-bold hover:border-[#dc2626] hover:text-[#dc2626] transition-colors disabled:opacity-50"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ),
          )}

          {/* Treatments the pro just ADDED that are still awaiting approval —
              they don't exist in the live list yet, so render them here so
              the pro can see their submission instead of an empty list. */}
          {pendingCreates.map((o, i) => (
            <div
              key={`pending-create-${i}`}
              className="flex items-center justify-between gap-3 bg-[#fffdf7] px-6 py-4"
            >
              <div className="min-w-0">
                <p className="font-bold text-[15px] text-[#3a3335] truncate">
                  {o.name}
                  <span className="ml-2 inline-block rounded-full bg-[#fff7e6] px-2 py-0.5 text-[10px] font-bold text-[#9a6700] align-middle ring-1 ring-inset ring-[#f3d6a0]">
                    Pending approval
                  </span>
                </p>
                <p className="text-[13px] text-[#908e8e]">
                  {money(o.price)} · {formatDuration(o.duration_minutes)}
                </p>
              </div>
            </div>
          ))}

          {editingId === 'new' ? (
            <TreatmentForm
              draft={draft}
              setDraft={setDraft}
              categories={categories}
              onSave={save}
              onCancel={cancel}
              saving={saving}
            />
          ) : null}
        </div>
      )}
    </section>
  );
}

function TreatmentForm({
  draft,
  setDraft,
  categories,
  onSave,
  onCancel,
  saving,
}: {
  draft: TreatmentDraft;
  setDraft: (d: TreatmentDraft) => void;
  categories: TreatmentCategoryOption[];
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
}) {
  return (
    <div className="px-6 py-4 bg-[#fcfaf7] space-y-3">
      <label className="block">
        <span className="block text-[11px] font-bold text-[#908e8e] mb-1">
          Category
        </span>
        <ThemedSelect
          value={draft.service_category_id}
          onChange={(v) => setDraft({ ...draft, service_category_id: v })}
          options={categories.map((c) => ({ value: c.id, label: c.name }))}
          buttonClassName="w-full"
        />
      </label>
      <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_1fr] gap-3">
        <Field
          label="Name"
          placeholder="e.g. Balayage"
          value={draft.name}
          onChange={(v) => setDraft({ ...draft, name: v })}
        />
        <Field
          label="Price (£)"
          placeholder="75"
          inputMode="decimal"
          value={draft.price}
          onChange={(v) =>
            /^[0-9]*\.?[0-9]{0,2}$/.test(v) || v === ''
              ? setDraft({ ...draft, price: v })
              : undefined
          }
        />
        <Field
          label="Time (mins)"
          placeholder="90"
          inputMode="numeric"
          value={draft.duration_minutes}
          onChange={(v) =>
            /^[0-9]*$/.test(v)
              ? setDraft({ ...draft, duration_minutes: v })
              : undefined
          }
        />
      </div>
      {/* Patch-test flag — when on, clients booking this treatment must
          confirm the 48-hour patch-test requirement at checkout. */}
      <label className="flex items-start gap-2 rounded-[8px] border border-[#ede8e9] bg-white px-3 py-2.5 cursor-pointer">
        <input
          type="checkbox"
          checked={draft.requires_patch_test}
          onChange={(e) =>
            setDraft({ ...draft, requires_patch_test: e.target.checked })
          }
          className="mt-0.5 w-4 h-4 accent-[#8e2a2f] cursor-pointer shrink-0"
        />
        <span className="text-[13px] text-[#3a3335]">
          <span className="font-bold">This service requires a patch test</span>
          <span className="block text-[12px] text-[#908e8e]">
            Clients booking this treatment must confirm the 48-hour patch-test
            requirement before they can pay.
          </span>
        </span>
      </label>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="h-[40px] px-5 rounded-[8px] bg-[#8e2a2f] text-white text-[13px] font-bold hover:bg-[#7a2228] transition-colors disabled:opacity-60"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="h-[40px] px-5 rounded-[8px] border border-[#ede8e9] text-[#908e8e] text-[13px] font-bold hover:text-[#3a3335] transition-colors disabled:opacity-60"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  inputMode,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  inputMode?: 'decimal' | 'numeric';
}) {
  return (
    <label className="block">
      <span className="block text-[11px] font-bold text-[#908e8e] mb-1">
        {label}
      </span>
      <input
        type="text"
        inputMode={inputMode}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-[44px] px-3 rounded-[8px] border border-[#ede8e9] bg-white text-[14px] font-bold text-[#3a3335] outline-none focus:border-[#8e2a2f]"
      />
    </label>
  );
}
